import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import CustomModal from '../atoms/CustomModal';
import InfoText from '../atoms/InfoText';
import FormField from '../FormField/FormField';
import Button from '../atoms/Button';

export interface BiddingPostsFilterValues {
  status: string[];
  dateFrom: string;
  dateTo: string;
}

interface FormInternalValues {
  status: string[];
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
}

interface BiddingPostsFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: BiddingPostsFilterValues;
  onApply: (filters: BiddingPostsFilterValues) => void;
  onReset: () => void;
}

export default function BiddingPostsFilterModal({
  isOpen,
  onClose,
  filters,
  onApply,
  onReset,
}: BiddingPostsFilterModalProps) {
  const { t } = useTranslation();

  const statusOptions = [
    { label: t('biddingPosts.statusNew'), value: 'PUBLISHED' },
    { label: t('biddingPosts.statusInProgress'), value: 'ONGOING' },
    { label: t('biddingPosts.statusCompleted'), value: 'COMPLETED' },
  ];

  const schema = z
    .object({
      status: z.array(z.string()).optional(),
      dateFrom: z.custom<Dayjs>().nullable().optional(),
      dateTo: z.custom<Dayjs>().nullable().optional(),
    })
    .superRefine(({ dateFrom, dateTo }, ctx) => {
      const today = dayjs().endOf('day');
      if (dateFrom && dateFrom.isAfter(today, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('biddingPosts.dateFutureError'),
          path: ['dateFrom'],
        });
      }
      if (dateTo && dateTo.isAfter(today, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('biddingPosts.dateFutureError'),
          path: ['dateTo'],
        });
      }
      if (dateFrom && dateTo && dateTo.isBefore(dateFrom, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('dispute.dateToBeforeFrom'),
          path: ['dateTo'],
        });
      }
    });

  const methods = useForm<FormInternalValues>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      status: [],
      dateFrom: null,
      dateTo: null,
    },
  });

  useEffect(() => {
    if (isOpen) {
      methods.reset({
        status: filters.status || [],
        dateFrom: filters.dateFrom ? dayjs(filters.dateFrom) : null,
        dateTo: filters.dateTo ? dayjs(filters.dateTo) : null,
      });
    }
  }, [isOpen, filters, methods]);

  const watchAll = methods.watch();

  const appliedFiltersCount = [
    !!watchAll.status?.length,
    watchAll.dateFrom,
    watchAll.dateTo,
  ].filter(Boolean).length;

  const disabledDateFrom = (current: Dayjs) => {
    if (!current) return false;
    const isFuture = current.isAfter(dayjs(), 'day');
    const isAfterDateTo = !!watchAll.dateTo && current.isAfter(watchAll.dateTo, 'day');
    return isFuture || isAfterDateTo;
  };

  const disabledDateTo = (current: Dayjs) => {
    if (!current) return false;
    const isFuture = current.isAfter(dayjs(), 'day');
    const isBeforeDateFrom = !!watchAll.dateFrom && current.isBefore(watchAll.dateFrom, 'day');
    return isFuture || isBeforeDateFrom;
  };

  const handleReset = () => {
    methods.reset({
      status: [],
      dateFrom: null,
      dateTo: null,
    });
    onReset();
    onClose();
  };

  const onSubmit = (values: FormInternalValues) => {
    const fromDateStr = values.dateFrom ? values.dateFrom.format('YYYY-MM-DD') : '';
    const toDateStr = values.dateTo ? values.dateTo.format('YYYY-MM-DD') : '';

    onApply({
      status: values.status || [],
      dateFrom: fromDateStr,
      dateTo: toDateStr,
    });
    onClose();
  };

  return (
    <CustomModal
      title={t('home.filters')}
      isOpen={isOpen}
      onClose={onClose}
      subTitle={
        <div className="flex items-center gap-2" data-testid="bidding-posts-filter-subtitle">
          <InfoText
            size="sm"
            text={`${appliedFiltersCount} ${t('home.filterApplied')}`}
            data-testid="bidding-posts-filter-count"
          />
          <button
            type="button"
            onClick={handleReset}
            className="text-light-success hover:underline text-sm font-semibold cursor-pointer"
            data-testid="bidding-posts-filter-reset-btn"
          >
            {t('home.resetFilter')}
          </button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="space-y-6"
          data-testid="bidding-posts-filter-form"
        >
          {/* Post Status Section */}
          <div className="space-y-2" data-testid="bidding-posts-filter-status-section">
            <InfoText
              text={t('biddingPosts.postStatus')}
              className="!font-semibold !text-base text-light-text-primary dark:text-dark-text-primary"
              data-testid="bidding-posts-filter-status-label"
            />
            <FormField
              name="status"
              id="status"
              type="select"
              placeholder={t('home.selectMethod')}
              options={statusOptions}
              mode="multiple"
              allowClear
              data-testid="bidding-posts-filter-status-select"
            />
          </div>

          <div
            className="border-t border-light-input-border dark:border-dark-input-border"
            data-testid="bidding-posts-filter-divider"
          />

          {/* Date Range Section */}
          <div className="grid grid-cols-2 gap-x-4" data-testid="bidding-posts-filter-dates-section">
            <FormField
              name="dateFrom"
              id="dateFrom"
              type="date"
              label={t('home.dateFrom')}
              placeholder="dd/mm/yyyy"
              allowClear
              disabledDate={disabledDateFrom}
              maxDate={dayjs()}
              data-testid="bidding-posts-filter-date-from"
            />
            <FormField
              name="dateTo"
              id="dateTo"
              type="date"
              label={t('home.dateTo')}
              placeholder="dd/mm/yyyy"
              allowClear
              disabledDate={disabledDateTo}
              maxDate={dayjs()}
              data-testid="bidding-posts-filter-date-to"
            />
          </div>

          {/* Actions */}
          <div
            className="flex items-center justify-end gap-x-4 pt-4"
            data-testid="bidding-posts-filter-actions"
          >
            <Button
              variant="outline"
              onClick={onClose}
              className="min-w-[120px] h-[48px]"
              data-testid="bidding-posts-filter-cancel-btn"
            >
              {t('home.cancel')}
            </Button>
            <Button
              type="submit"
              variant="dark"
              className="min-w-[120px] h-[48px]"
              data-testid="bidding-posts-filter-apply-btn"
            >
              {t('home.save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </CustomModal>
  );
}
